/**
 * Human-readable labels for purchase history rows (quotes vs invoices).
 */

import { isQuoteConverted } from './portalBillingDocuments.js'
import {
  formatBillingCalendarDate,
  isQuoteExpired,
  isInvoiceOverdue,
} from './portalBillingDocumentDates.js'
import { findPendingExtensionRequestForDocument } from './portalBillingDocumentExtensionRequests.js'

export const INITIAL_SEAT_POOL_LABEL = 'Initial seat pool'
export const INITIAL_SEAT_POOL_SUBTITLE = 'Seats on your account at portal launch'
export const PURCHASE_HISTORY_SEAT_NA_TOOLTIP =
  'Not applicable — this record has no seat licenses'

/**
 * Pull NFHS publication title×learner assignment details from a purchase-history
 * entry / billing document (line metadata or cartSnapshot).
 * @param {Record<string, unknown> | null | undefined} entry
 * @returns {{
 *   bookId: string,
 *   bookTitle: string,
 *   bookSportLabel: string,
 *   kind: string,
 *   quantity: number,
 *   emails: string[],
 *   unitPrice: number | null,
 *   amount: number | null,
 *   assigned: boolean,
 *   pending: boolean,
 * } | null}
 */
export function resolveNfhsPublicationAssignPurchase(entry) {
  if (!entry || typeof entry !== 'object') return null
  const lineItems = Array.isArray(entry.lineItems)
    ? entry.lineItems
    : Array.isArray(entry.billingDocument?.lineItems)
      ? entry.billingDocument.lineItems
      : []
  const line = lineItems.find((row) => String(row?.key || '').trim() === 'nfhs_publication_assign')
  const snap =
    entry.billingDocument?.cartSnapshot?.nfhsPublicationAssign
    || entry.cartSnapshot?.nfhsPublicationAssign
    || null
  if (!line && !snap && !entry.hasNfhsPublicationAssignLine) return null

  const meta = line?.metadata && typeof line.metadata === 'object' ? line.metadata : {}
  const emailSet = new Set()
  for (const list of [meta.emails, snap?.emails, entry.nfhsPublicationAssignEmails]) {
    if (!Array.isArray(list)) continue
    for (const raw of list) {
      const email = String(raw || '').trim().toLowerCase()
      if (email) emailSet.add(email)
    }
  }
  const emails = [...emailSet]
  const quantity =
    emails.length
    || Number(line?.quantity)
    || Number(snap?.quantity)
    || Number(entry.nfhsPublicationAssignQuantity)
    || 0
  if (!(quantity > 0) && !emails.length && !line && !snap) return null

  const fulfillment = String(
    entry.fulfillmentStatus
    ?? entry.billingDocument?.fulfillmentStatus
    ?? ''
  ).trim().toLowerCase()
  const status = String(entry.status ?? entry.billingDocument?.status ?? '').trim().toLowerCase()
  const assigned =
    fulfillment === 'fulfilled'
    || Boolean(entry.fulfilledAt || entry.billingDocument?.fulfilledAt)
    || status === 'paid'
    || Boolean(entry.paidAt || entry.billingDocument?.paidAt)

  return {
    bookId: String(meta.bookId || snap?.bookId || '').trim(),
    bookTitle: String(
      meta.bookTitle || snap?.bookTitle || line?.item || 'NFHS publication'
    ).trim(),
    bookSportLabel: String(meta.bookSportLabel || snap?.bookSportLabel || meta.bookSport || '').trim(),
    kind: String(meta.nfhsAddonKind || meta.kind || snap?.kind || '').trim(),
    quantity,
    emails,
    unitPrice: Number.isFinite(Number(line?.unitPrice ?? snap?.unitPrice))
      ? Number(line?.unitPrice ?? snap?.unitPrice)
      : null,
    amount: Number.isFinite(Number(line?.amount))
      ? Number(line.amount)
      : null,
    assigned,
    pending: !assigned,
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} entry
 * @param {unknown[]} [students]
 */
export function buildNfhsPublicationAssignLearnerRows(entry, students = []) {
  const detail = resolveNfhsPublicationAssignPurchase(entry)
  if (!detail) return { detail: null, learners: [] }

  const byEmail = new Map()
  for (const student of Array.isArray(students) ? students : []) {
    const email = String(student?.info?.email || student?.email || '').trim().toLowerCase()
    if (!email || byEmail.has(email)) continue
    byEmail.set(email, {
      email,
      name: String(student?.info?.name || student?.name || '').trim() || email,
      profileImage: String(student?.info?.profileImage || student?.profileImage || '').trim(),
    })
  }

  const learners = detail.emails.map((email) => {
    const known = byEmail.get(email)
    if (known) return known
    return {
      email,
      name: email,
      profileImage: '',
    }
  })

  return { detail, learners }
}

const INITIAL_SEAT_POOL_SOURCE_TYPES = new Set([
  'migration',
  'legacy_backfill',
  'legacy_migration',
])

/** @param {string | null | undefined} sourceType */
export function isInitialSeatPoolSourceType(sourceType) {
  return INITIAL_SEAT_POOL_SOURCE_TYPES.has(String(sourceType || '').toLowerCase())
}

/** @param {Record<string, unknown> | null | undefined} batch */
export function isInitialSeatPoolBatch(batch) {
  return batch != null && isInitialSeatPoolSourceType(batch.sourceType)
}

/**
 * Pre-portal seat pool — stable label even when linked to a conversion invoice.
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown> | null | undefined} batch
 */
export function isInitialSeatPoolEntry(entry, batch) {
  if (isInitialSeatPoolBatch(batch)) return true
  if (entry?.isSeatBatchRow && isInitialSeatPoolSourceType(entry.sourceType)) return true
  return isInitialSeatPoolSourceType(entry?.sourceType)
}

/**
 * Primary label for the purchase history Reference column.
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown> | null | undefined} batch
 */
export function resolvePurchaseHistoryRowLabel(entry, batch) {
  if (isInitialSeatPoolEntry(entry, batch)) {
    return INITIAL_SEAT_POOL_LABEL
  }
  return entry?.number || '—'
}

/**
 * Optional subtitle under the Reference label (initial seat pool only).
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown> | null | undefined} batch
 */
export function resolvePurchaseHistoryRowReferenceSubtitle(entry, batch) {
  if (isInitialSeatPoolEntry(entry, batch)) {
    return INITIAL_SEAT_POOL_SUBTITLE
  }
  if (entry?.isLegacyOrganizationPurchase) {
    const orderType = String(
      entry.legacyOrderType ??
      entry.legacyPurchase?.orderType ??
      entry.legacyPurchase?.type ??
      ''
    ).toLowerCase()
    if (orderType === 'gift') return 'Gifted to your organization'
    if (orderType === "po'd" || orderType === 'pod' || orderType === 'po') {
      return "PO'd for your organization"
    }
  }
  return null
}

function formatLicenseLabel(type) {
  const raw = String(type ?? '').trim()
  if (!raw) return ''
  const key = raw.toLowerCase()
  if (key === 'premium') return 'Premium'
  if (key === 'basic') return 'Basic'
  if (key === 'course access' || key === 'courseaccess') return 'Course Access'
  return raw
}

function getLineItems(entry) {
  if (Array.isArray(entry?.lineItems) && entry.lineItems.length) {
    return entry.lineItems
  }
  const doc = entry?.billingDocument
  if (Array.isArray(doc?.lineItems)) return doc.lineItems
  return []
}

function conversionLineFromEntry(entry) {
  return getLineItems(entry).find((row) => row?.key === 'conversion') ?? null
}

/** @param {Record<string, unknown> | null | undefined} entry */
export function isPurchaseHistoryQuoteRow(entry) {
  return entry?.documentType === 'quote' || entry?.type === 'Estimate/Quote'
}

/** @param {Record<string, unknown> | null | undefined} entry */
export function isPurchaseHistoryInvoiceRow(entry) {
  return entry?.documentType === 'invoice' || entry?.type === 'Invoice'
}

/**
 * Paid at column — portal invoices only.
 * @param {Record<string, unknown> | null | undefined} entry
 */
export function formatPurchaseHistoryPaidAt(entry) {
  if (!entry || !isPurchaseHistoryInvoiceRow(entry)) return '—'
  const status = String(
    entry.status ?? entry.billingDocument?.status ?? ''
  ).toLowerCase()
  if (status === 'po_received' || status === 'overdue') return '—'
  const isPaid =
    status === 'paid' ||
    String(entry.fulfillmentStatus ?? entry.billingDocument?.fulfillmentStatus ?? '')
      .toLowerCase() === 'fulfilled'
  if (!isPaid) return '—'
  const paidAt = entry.paidAt ?? entry.billingDocument?.paidAt
  if (paidAt) return formatBillingCalendarDate(paidAt)
  const fulfilledAt = entry.fulfilledAt ?? entry.billingDocument?.fulfilledAt
  if (fulfilledAt) return formatBillingCalendarDate(fulfilledAt)
  return '—'
}

/** @param {Record<string, unknown> | null | undefined} entry */
export function isPurchaseHistoryQuoteInvoiced(entry) {
  const doc =
    entry?.billingDocument ??
    (entry?.documentType === 'quote' || entry?.type === 'Estimate/Quote'
      ? entry
      : null)
  if (doc) {
    return isQuoteConverted(doc)
  }
  return String(entry?.status || '').toLowerCase() === 'converted'
}

/**
 * @param {Record<string, unknown>} entry
 * @param {'all' | 'invoiced' | 'uninvoiced'} filterType
 */
export function matchesPurchaseHistoryQuoteStatusFilter(entry, filterType) {
  if (filterType === 'all') return true
  const invoiced = isPurchaseHistoryQuoteInvoiced(entry)
  if (filterType === 'invoiced') return invoiced
  if (filterType === 'uninvoiced') return !invoiced
  return true
}

/**
 * Quote status — uninvoiced until converted; expired quotes are locked.
 * @param {Record<string, unknown>} entry
 * @param {Array<Record<string, unknown>>} [extensionRequests]
 */
export function resolveQuotePurchaseHistoryStatusBadge(entry, extensionRequests = []) {
  const doc = entry?.billingDocument ?? entry
  const documentId = entry?.documentId ?? doc?.id ?? doc?._id
  const pending = findPendingExtensionRequestForDocument(
    extensionRequests,
    documentId,
    'quote'
  )
  if (pending) {
    return {
      label: 'Extension pending',
      class: 'portal-ph-table__status--pending',
      title: 'Awaiting RefReps approval for a new expiration date.',
    }
  }

  if (isQuoteExpired(doc ?? entry)) {
    return {
      label: 'Expired',
      class: 'portal-ph-table__status--expired',
      title: 'Past valid-until date. Request an extension to edit or invoice.',
    }
  }

  const isInvoiced = isPurchaseHistoryQuoteInvoiced(entry)

  if (isInvoiced) {
    return {
      label: 'Invoiced',
      class: 'portal-ph-table__status--seats-active',
      title: 'This quote was converted to an invoice.',
    }
  }

  return {
    label: 'Uninvoiced',
    class: 'portal-ph-table__status--doc-converted',
    title: 'This quote has not been converted to an invoice yet.',
  }
}

/** @param {Record<string, unknown> | null | undefined} entry */
function resolvePurchaseHistoryBillingStatus(entry) {
  return String(entry?.status ?? entry?.billingDocument?.status ?? '').toLowerCase()
}

/**
 * Paid / unpaid status for invoices — details belong in What's included.
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown> | null | undefined} batch
 * @param {boolean} [paid]
 * @param {Array<Record<string, unknown>>} [extensionRequests]
 */
export function resolvePurchaseHistoryPaidStatusBadge(
  entry,
  batch,
  paid = false,
  extensionRequests = []
) {
  if (isPurchaseHistoryQuoteRow(entry)) {
    return resolveQuotePurchaseHistoryStatusBadge(entry, extensionRequests)
  }

  const status = resolvePurchaseHistoryBillingStatus(entry)
  const doc = entry?.billingDocument ?? entry
  const documentId = entry?.documentId ?? doc?.id ?? doc?._id
  const pendingExtension = findPendingExtensionRequestForDocument(
    extensionRequests,
    documentId,
    'invoice'
  )

  if (pendingExtension) {
    return {
      label: 'Extension pending',
      class: 'portal-ph-table__status--pending',
      title: 'Awaiting RefReps approval for a new due date.',
    }
  }

  // Overdue wins over Paid. A PO-received invoice is fulfilled (seats granted)
  // before payment, so a linked seat batch must not label a later overdue status as Paid.
  if (status === 'overdue' || isInvoiceOverdue(entry)) {
    return {
      label: 'Overdue',
      class: 'portal-ph-table__status--expired',
      title: 'Past due date. Payment is still accepted in QuickBooks.',
    }
  }

  if (status === 'voided' || status === 'void') {
    return {
      label: 'Voided',
      class: 'portal-ph-table__status--awaiting',
      title: null,
    }
  }

  if (status === 'po_received') {
    return {
      label: 'PO Received',
      class: 'portal-ph-table__status--pending',
      title: 'Purchase order received — awaiting payment.',
    }
  }

  if (batch || entry?.isSeatBatchRow || entry?.documentType === 'seat_batch') {
    return {
      label: 'Paid',
      class: 'portal-ph-table__status--seats-active',
      title: null,
    }
  }

  if (entry?.type === 'Payment' || entry?.documentType === 'payment') {
    return {
      label: 'Paid',
      class: 'portal-ph-table__status--seats-active',
      title: null,
    }
  }

  const isPaid =
    paid ||
    status === 'paid' ||
    String(entry?.fulfillmentStatus || '').toLowerCase() === 'fulfilled'

  return {
    label: isPaid ? 'Paid' : 'Unpaid',
    class: isPaid
      ? 'portal-ph-table__status--seats-active'
      : 'portal-ph-table__status--awaiting',
    title: null,
  }
}

/**
 * @param {Record<string, unknown>} entry
 */
export function describePurchaseHistoryRowContents(entry) {
  const lineItems = getLineItems(entry)
  const parts = []

  const conversionLine = conversionLineFromEntry(entry)
  const hasConversion =
    Boolean(entry?.hasConversionLine) || Boolean(conversionLine)

  if (hasConversion) {
    const from = formatLicenseLabel(
      entry?.conversionFromLicense ?? conversionLine?.metadata?.fromLicense
    )
    const to = formatLicenseLabel(
      entry?.conversionToLicense ?? conversionLine?.metadata?.toLicense
    )
    const qty = Number(entry?.conversionSeats ?? conversionLine?.quantity) || 0
    let text = conversionLine?.item || 'License conversion'
    if (from && to) {
      text = `License conversion (${from} → ${to})`
    }
    const detail =
      qty > 0
        ? `${qty} license${qty !== 1 ? 's' : ''} converted`
        : 'License type change — no new seats purchased'
    parts.push({ type: 'conversion', text, detail })
  }

  const seatQty = Number(entry?.seats) || 0
  if (seatQty > 0) {
    const license = formatLicenseLabel(entry?.license)
    parts.push({
      type: 'seats',
      text: `${seatQty} seat license${seatQty !== 1 ? 's' : ''}`,
      detail: license ? `${license} license` : 'New seats for course assignments',
    })
  }

  const suppLine = lineItems.find((row) => row?.key === 'supplemental_packs')
  if (suppLine) {
    const qty = Number(suppLine.quantity) || 0
    parts.push({
      type: 'supplemental',
      text: `${qty || ''} supplemental pack${qty !== 1 ? 's' : ''}`.trim(),
      detail: 'POV / supplemental video packs',
    })
  }

  const nfhsLine = lineItems.find((row) => row?.key === 'nfhs')
  if (nfhsLine) {
    parts.push({
      type: 'nfhs',
      text: 'NFHS digital publications',
      detail: 'Digital rule books add-on',
    })
  }

  const publicationAssign = resolveNfhsPublicationAssignPurchase(entry)
  if (publicationAssign) {
    const qty = publicationAssign.quantity
    const title = publicationAssign.bookTitle
    parts.push({
      type: 'nfhs_publication_assign',
      text:
        qty > 0
          ? `${qty} publication assignment${qty !== 1 ? 's' : ''}`
          : 'Publication assignment',
      detail: title
        ? `"${title}" · one copy per selected learner`
        : 'Title × learner NFHS Digital assignment',
    })
  }

  const inProgressLine = lineItems.find((row) => row?.key === 'in_progress_seat_upgrade')
  const hasInProgressUpgrade = Boolean(inProgressLine)
  if (inProgressLine) {
    const qty = Number(inProgressLine.quantity) || 0
    const companion = inProgressLine?.metadata?.conversionCompanion === true
    let detail = 'Upgrade fee for learners already in progress'
    if (companion) {
      detail = 'In-progress seats included with this license conversion'
    } else if (seatQty === 0) {
      detail = 'Upgrade fee for learners already in progress — no new seats purchased'
    }
    parts.push({
      type: 'in_progress_upgrade',
      text: `${qty || ''} in-progress seat upgrade${qty !== 1 ? 's' : ''}`.trim(),
      detail,
    })
  }

  const otherLines = lineItems.filter(
    (row) =>
      row?.key &&
      ![
        'conversion',
        'seats',
        'supplemental_packs',
        'nfhs',
        'nfhs_publication_assign',
        'in_progress_seat_upgrade',
      ].includes(String(row.key))
  )
  for (const row of otherLines) {
    parts.push({
      type: 'other',
      text: String(row.item || row.description || 'Line item'),
      detail: null,
    })
  }

  const summary = parts.map((p) => p.text).join(' · ')
  const primaryType = parts[0]?.type ?? (seatQty ? 'seats' : 'other')
  const isConversionOnly = hasConversion && seatQty === 0 && !hasInProgressUpgrade
  const isSeatsOnly = seatQty > 0 && !hasConversion

  const notes = []
  if (!isPurchaseHistoryQuoteRow(entry)) {
    if (isConversionOnly) {
      notes.push('No new seats on this invoice')
    } else if (hasInProgressUpgrade && seatQty === 0 && !hasConversion) {
      notes.push('No new seats on this invoice')
    } else if (!seatQty && !hasConversion && !hasInProgressUpgrade && parts.length === 0) {
      notes.push('No seat licenses')
    }
  }

  return {
    parts,
    summary: summary || 'No line-item details available',
    notes,
    primaryType,
    isConversionOnly,
    isSeatsOnly,
    hasConversion,
    hasSeats: seatQty > 0,
  }
}

/**
 * Extra detail lines for the What's included info popup (not shown inline in the row).
 * @param {ReturnType<typeof describePurchaseHistoryRowContents>} contents
 */
export function buildPurchaseHistoryContentsDetailLines(contents) {
  const lines = []
  for (const part of contents.parts) {
    if (part.detail) {
      lines.push(`${part.text} — ${part.detail}`)
    }
  }
  for (const note of contents.notes) {
    lines.push(note)
  }
  return lines
}

/**
 * License column — value only; context lives in What's included.
 * @param {Record<string, unknown>} entry
 * @param {{ license?: string }} seatDisplay
 */
export function resolvePurchaseHistoryLicenseDisplay(entry, seatDisplay) {
  const contents = describePurchaseHistoryRowContents(entry)
  if (contents.hasConversion) {
    const conversionLine = conversionLineFromEntry(entry)
    const from = formatLicenseLabel(
      entry?.conversionFromLicense ?? conversionLine?.metadata?.fromLicense
    )
    const to = formatLicenseLabel(
      entry?.conversionToLicense ?? conversionLine?.metadata?.toLicense
    )
    if (from && to) return `${from} → ${to}`
  }
  const license = seatDisplay?.license || entry?.license
  if (!license || license === '—') return '—'
  return String(license)
}
